/**
 * EntityEnrichmentService
 * -----------------------
 * Enriches automatically-created accommodations and activities (from email
 * processing, AI trip planner, AI improvements) with:
 *   1. Geocoordinates  → location.lat/lng
 *   2. A highlight image → images[0]   (accommodations)
 *                       → highlightImages[0] (activities)
 *
 * Called after any automated entity creation so users always see a map pin
 * and a photo without having to open the editor.
 *
 * Uses the Google Places New API (v1) via direct HTTP — no requirement for the
 * Maps JS SDK to be loaded, so it works safely inside background services.
 */

import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { ImageStorageService } from './image-storage.service';
import { ApiClient } from './api-client.service';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface EnrichmentResult {
    latitude?: number;
    longitude?: number;
    photoUrl?: string;
    /** true if at least coordinates were resolved */
    success: boolean;
}

// ─── Country bounding boxes (approximate) for coordinate validation ───────────
// Format: [minLat, maxLat, minLng, maxLng]
const COUNTRY_BOUNDS: Record<string, [number, number, number, number]> = {
    AF: [29.3, 38.5, 60.5, 74.9], AL: [39.6, 42.7, 19.3, 21.1], DZ: [18.9, 37.1, -8.7, 12.0],
    AD: [42.4, 42.7, 1.4, 1.8], AO: [-18.1, -4.4, 11.7, 24.1], AR: [-55.1, -21.8, -73.6, -53.6],
    AM: [38.8, 41.3, 43.4, 46.6], AU: [-43.6, -10.7, 113.2, 153.6], AT: [46.4, 49.0, 9.5, 17.2],
    AZ: [38.4, 41.9, 44.8, 50.4], BH: [25.8, 26.3, 50.4, 50.7], BD: [20.7, 26.6, 88.0, 92.7],
    BE: [49.5, 51.5, 2.5, 6.4], BZ: [15.9, 18.5, -89.2, -87.8], BJ: [6.2, 12.4, 0.8, 3.9],
    BT: [26.7, 28.3, 88.7, 92.1], BO: [-22.9, -9.7, -69.7, -57.5], BA: [42.6, 45.3, 15.7, 19.7],
    BW: [-26.9, -17.8, 20.0, 29.4], BR: [-33.8, 5.3, -73.1, -29.3], BN: [4.0, 5.1, 114.1, 115.4],
    BG: [41.2, 44.2, 22.4, 28.6], CA: [41.7, 83.1, -141.0, -52.6], CL: [-55.9, -17.5, -75.6, -66.4],
    CN: [18.2, 53.6, 73.5, 134.8], CO: [-4.2, 12.5, -79.0, -66.9], CR: [8.0, 11.2, -85.9, -82.6],
    HR: [42.4, 46.6, 13.5, 19.4], CU: [19.8, 23.3, -84.9, -74.1], CY: [34.6, 35.7, 32.3, 34.0],
    CZ: [48.6, 51.1, 12.1, 18.9], DK: [54.6, 57.8, 8.1, 15.2], DO: [17.5, 20.0, -72.0, -68.3],
    EC: [-5.0, 1.5, -80.9, -75.2], EG: [22.0, 31.7, 24.7, 37.2], SV: [13.2, 14.5, -90.1, -87.7],
    EE: [57.5, 59.7, 21.8, 28.2], ET: [3.4, 15.0, 33.0, 47.9], FJ: [-20.7, -15.7, 177.3, -179.9],
    FI: [59.8, 70.1, 19.1, 31.6], FR: [41.3, 51.1, -5.1, 9.6], DE: [47.3, 55.1, 5.9, 15.0],
    GH: [4.7, 11.2, -3.3, 1.2], GR: [34.8, 41.8, 19.4, 29.6], GT: [13.7, 17.8, -92.2, -88.2],
    HN: [13.0, 16.1, -89.4, -83.2], HU: [45.7, 48.6, 16.1, 22.9], IS: [63.4, 66.5, -24.5, -13.5],
    IN: [8.1, 37.1, 68.2, 97.4], ID: [-11.0, 5.9, 95.0, 141.0], IR: [25.1, 39.8, 44.0, 63.3],
    IQ: [29.1, 37.4, 38.8, 48.6], IE: [51.4, 55.4, -10.5, -5.9], IL: [29.5, 33.3, 34.2, 35.9],
    IT: [35.5, 47.1, 6.6, 18.5], JP: [24.3, 45.5, 123.0, 145.8], JO: [29.2, 33.4, 35.0, 39.3],
    KZ: [40.6, 55.4, 50.3, 87.4], KE: [-4.7, 5.0, 33.9, 41.9], KW: [28.5, 30.1, 46.6, 48.4],
    LB: [33.1, 34.7, 35.1, 36.6], LY: [19.5, 33.2, 9.4, 25.2], LI: [47.0, 47.3, 9.5, 9.6],
    LT: [53.9, 56.5, 20.9, 26.8], LU: [49.4, 50.2, 5.7, 6.5], MK: [40.9, 42.4, 20.5, 23.0],
    MG: [-25.6, -12.1, 43.2, 50.5], MY: [0.9, 7.4, 99.6, 119.3], MV: [-0.7, 7.1, 72.6, 73.8],
    ML: [10.2, 25.0, -4.2, 4.3], MT: [35.8, 36.1, 14.2, 14.6], MX: [14.5, 32.7, -117.1, -86.7],
    MD: [45.5, 48.5, 26.6, 30.2], MC: [43.7, 43.8, 7.4, 7.5], MN: [41.6, 52.1, 87.7, 119.9],
    ME: [41.9, 43.6, 18.4, 20.4], MA: [27.7, 35.9, -13.2, -1.0], MZ: [-26.9, -10.5, 30.2, 40.9],
    NA: [-29.0, -17.0, 11.7, 25.3], NP: [26.4, 30.4, 80.1, 88.2], NL: [50.7, 53.6, 3.4, 7.2],
    NZ: [-47.3, -34.4, 166.4, 178.6], NI: [11.0, 15.0, -87.7, -83.2], NG: [4.3, 13.9, 2.7, 14.7],
    NO: [57.9, 71.2, 4.5, 31.1], OM: [16.6, 26.4, 51.8, 59.8], PK: [23.7, 37.1, 60.9, 77.2],
    PA: [7.2, 9.6, -83.1, -77.2], PG: [-10.7, -1.3, 140.8, 155.9], PY: [-27.6, -19.3, -62.7, -54.3],
    PE: [-18.4, -0.1, -81.4, -68.7], PH: [4.6, 20.9, 116.9, 126.6], PL: [49.0, 54.9, 14.1, 24.2],
    PT: [36.9, 42.2, -9.5, -6.2], QA: [24.5, 26.2, 50.8, 51.6], RO: [43.6, 48.3, 20.3, 30.0],
    RU: [41.2, 81.9, 19.6, 190.0], RW: [-2.8, -1.1, 29.0, 30.9], SA: [16.3, 32.2, 34.5, 55.7],
    SN: [12.3, 16.7, -17.5, -11.4], RS: [42.2, 46.2, 18.8, 23.0], SK: [47.7, 49.6, 16.8, 22.6],
    SI: [45.4, 46.9, 13.4, 16.6], SO: [-1.7, 12.0, 41.0, 51.4], ZA: [-34.8, -22.1, 16.5, 33.0],
    ES: [35.9, 43.8, -9.3, 4.3], LK: [5.9, 9.8, 79.7, 81.9], SD: [3.5, 22.0, 24.0, 38.6],
    SE: [55.4, 69.1, 11.1, 24.2], CH: [45.8, 47.8, 5.9, 10.5], SY: [32.3, 37.3, 35.7, 42.4],
    TW: [22.0, 25.3, 120.0, 122.0], TZ: [-11.7, -1.0, 29.3, 40.4], TH: [5.6, 20.5, 97.4, 105.6],
    TR: [36.0, 42.1, 26.0, 44.8], UA: [44.4, 52.4, 22.1, 40.2], AE: [22.6, 26.1, 51.6, 56.4],
    GB: [49.9, 60.9, -8.2, 1.8], US: [18.9, 71.4, -171.8, -66.9], UY: [-34.9, -30.1, -58.4, -53.1],
    UZ: [37.2, 45.6, 56.0, 73.1], VE: [0.6, 12.2, -73.4, -59.8], VN: [8.5, 23.4, 102.1, 109.5],
    YE: [12.6, 19.0, 42.5, 53.1], ZM: [-18.1, -8.2, 22.0, 33.7], ZW: [-22.4, -15.6, 25.2, 33.1],
};

/**
 * Validate that (lat, lng) falls within the approximate bounding box for the given country code(s).
 * Returns true if valid, false if clearly outside all specified countries.
 */
function isCoordinateInCountries(lat: number, lng: number, countryCodes: string[]): boolean {
    if (!countryCodes.length) return true; // no constraint
    for (const code of countryCodes) {
        const bounds = COUNTRY_BOUNDS[code.toUpperCase()];
        if (!bounds) return true; // unknown country — don't block
        const [minLat, maxLat, minLng, maxLng] = bounds;
        if (lat >= minLat && lat <= maxLat && lng >= minLng && lng <= maxLng) return true;
    }
    return false;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export class EntityEnrichmentService {

    // ── Core Places search ────────────────────────────────────────────────────

    /**
     * Search the Google Places API via the API gateway.
     * Accepts optional country codes/names to:
     *   1. Append the English country name to the query (crucial for Hebrew/RTL names)
     *   2. Pass a regionCode to Google Places for single-country trips
     *   3. Validate the returned coordinates are within the expected country bounds
     */
    private static async searchPlace(
        query: string,
        options?: { countryCodes?: string[]; countryNames?: string[] }
    ): Promise<{
        lat: number;
        lng: number;
        photoRef?: string;
        photoUri?: string;
        formattedAddress?: string;
    } | null> {
        const { countryCodes = [], countryNames = [] } = options ?? {};

        // Append the first English country name to the query so the Places API
        // correctly resolves Hebrew (or other RTL) names that could otherwise
        // match wrong locations (e.g. "קאדיאק" → Kodiak, Alaska instead of Cadillac, France).
        let resolvedQuery = query;
        if (countryNames.length > 0) {
            // Only append if the English country name is not already in the query
            const firstCountry = countryNames[0];
            if (!query.toLowerCase().includes(firstCountry.toLowerCase())) {
                resolvedQuery = `${query} ${firstCountry}`;
            }
        }

        // For single-country trips use regionCode to bias Google Places results
        const regionCode = countryCodes.length === 1 ? countryCodes[0].toLowerCase() : undefined;

        console.log('[EntityEnrichment] searchPlace → querying Places API (via gateway) for:', resolvedQuery,
            regionCode ? `(regionCode: ${regionCode})` : '');

        try {
            const requestBody: Record<string, unknown> = {
                textQuery: resolvedQuery,
                maxResultCount: 1,
            };
            if (regionCode) requestBody.regionCode = regionCode;

            const data = await ApiClient.post<{ places?: any[] }>('/places/search', requestBody);

            console.log('[EntityEnrichment] Places API response: placesCount=', data?.places?.length ?? 0);
            const place = data?.places?.[0];
            if (!place) {
                console.warn('[EntityEnrichment] No place found for query:', resolvedQuery);
                return null;
            }

            const lat: number = place.location?.latitude ?? 0;
            const lng: number = place.location?.longitude ?? 0;
            console.log('[EntityEnrichment] Place found: lat=%s lng=%s photosCount=%s', lat, lng, place.photos?.length ?? 0);

            // ── Coordinate validation ────────────────────────────────────────
            // Reject coordinates that fall outside all of the trip's countries.
            // This prevents wrong geocodes caused by Hebrew/RTL transliteration
            // ambiguity (e.g. "קאדיאק" matching Kodiak, Alaska instead of Cadillac, France).
            if (countryCodes.length > 0 && lat !== 0 && lng !== 0) {
                if (!isCoordinateInCountries(lat, lng, countryCodes)) {
                    console.warn(
                        `[EntityEnrichment] ⚠️ Coordinate validation FAILED for "${resolvedQuery}": ` +
                        `(${lat}, ${lng}) is outside countries [${countryCodes.join(', ')}]. ` +
                        `Retrying with country name in query…`
                    );
                    // Retry once with all country names explicitly in the query
                    const fallbackQuery = `${query} ${countryNames.join(' ')}`.trim();
                    if (fallbackQuery !== resolvedQuery) {
                        const retry = await this.searchPlace(fallbackQuery);
                        if (retry && isCoordinateInCountries(retry.lat, retry.lng, countryCodes)) {
                            console.log('[EntityEnrichment] ✅ Retry succeeded with valid coordinates:', retry.lat, retry.lng);
                            return retry;
                        }
                    }
                    console.warn('[EntityEnrichment] ❌ Dropping invalid coordinates — outside trip countries');
                    return null;
                }
            }

            const photoRef: string | undefined = place.photos?.[0]?.name;
            if (!photoRef) {
                console.warn('[EntityEnrichment] Place has no photos:', resolvedQuery);
            } else {
                console.log('[EntityEnrichment] photoRef:', photoRef);
            }

            // Build a photo proxy URL as CDN fallback
            let photoUri: string | undefined;
            if (photoRef) {
                const params = new URLSearchParams({
                    photoReference: photoRef,
                    maxHeight: '800',
                    maxWidth: '800',
                });
                photoUri = `${ApiClient.baseUrl}/places/photo?${params}`;
                console.log('[EntityEnrichment] gateway photo proxy URI:', photoUri);
            }

            // Already in the gateway's default field mask, so it costs nothing
            // extra and gives items created without an address a real one.
            const formattedAddress: string | undefined =
                typeof place.formattedAddress === 'string' && place.formattedAddress.trim()
                    ? place.formattedAddress.trim()
                    : undefined;

            return { lat, lng, photoRef, photoUri, formattedAddress };
        } catch (err) {
            console.warn('[EntityEnrichment] Places search failed:', err);
            return null;
        }
    }

    /**
     * Coordinates → street address via the API gateway (Google Geocoding API).
     *
     * Sources like a Google My Maps export carry only a pin, so this is the only
     * way those items get an address — which the editors, the search field and
     * navigation all rely on.
     */
    private static async reverseGeocode(lat: number, lng: number): Promise<string | undefined> {
        try {
            const data = await ApiClient.get<{ results?: any[]; status?: string }>(
                `/maps/geocode?latlng=${encodeURIComponent(`${lat},${lng}`)}`
            );
            const address = data?.results?.[0]?.formatted_address;
            if (typeof address !== 'string' || !address.trim()) {
                console.warn('[EntityEnrichment] Reverse geocode returned no address for:', lat, lng, 'status:', data?.status);
                return undefined;
            }
            console.log('[EntityEnrichment] ✅ Reverse geocoded (%s, %s) →', lat, lng, address);
            return address.trim();
        } catch (err) {
            console.warn('[EntityEnrichment] Reverse geocode failed:', err);
            return undefined;
        }
    }

    /**
     * Geocode a plain address string to coordinates via the API gateway
     * (Google Geocoding API). Used as a fallback when the Places text search
     * can't resolve a listing by name. Validates the result against the trip's
     * country bounds when provided.
     */
    private static async geocodeAddress(
        address: string,
        options?: { countryCodes?: string[]; countryNames?: string[] }
    ): Promise<{ lat: number; lng: number } | null> {
        const { countryCodes = [], countryNames = [] } = options ?? {};

        // Append the English country name when it's missing, to disambiguate.
        let resolved = address;
        if (countryNames.length > 0 && !address.toLowerCase().includes(countryNames[0].toLowerCase())) {
            resolved = `${address}, ${countryNames[0]}`;
        }

        console.log('[EntityEnrichment] geocodeAddress → querying Geocoding API (via gateway) for:', resolved);

        try {
            const data = await ApiClient.get<{ results?: any[]; status?: string }>(
                `/maps/geocode?address=${encodeURIComponent(resolved)}`
            );
            const loc = data?.results?.[0]?.geometry?.location;
            if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') {
                console.warn('[EntityEnrichment] Geocode returned no results for:', resolved, 'status:', data?.status);
                return null;
            }

            const { lat, lng } = loc;
            if (countryCodes.length > 0 && !isCoordinateInCountries(lat, lng, countryCodes)) {
                console.warn(`[EntityEnrichment] ⚠️ Geocoded (${lat}, ${lng}) is outside trip countries [${countryCodes.join(', ')}] — dropping`);
                return null;
            }

            console.log('[EntityEnrichment] ✅ Geocoded address → lat=%s lng=%s', lat, lng);
            return { lat, lng };
        } catch (err) {
            console.warn('[EntityEnrichment] Geocode failed:', err);
            return null;
        }
    }

    // ── Accommodation enrichment ──────────────────────────────────────────────

    /**
     * Enrich an accommodation document with geocoordinates and a cover photo.
     *
     * @param tripId               — Firestore trip document ID
     * @param accommodationId      — Firestore accommodation document ID
     * @param name                 — Property name (e.g. "ibis budget Lyon")
     * @param address              — Address string (can be empty / undefined)
     * @param tripCountries        — ISO codes + English names of the trip's countries
     *                               used to validate returned coordinates and bias the search
     * @param options.sourceCoordinates — Coordinates supplied by the source (e.g. KML
     *                               placemarks) are authoritative: the pin is never
     *                               moved, and a missing address is resolved from them.
     */
    static async enrichAccommodation(
        tripId: string,
        accommodationId: string,
        name: string,
        address?: string,
        tripCountries?: { code: string; name: string }[],
        options?: { sourceCoordinates?: { lat: number; lng: number } }
    ): Promise<EnrichmentResult> {
        const source = options?.sourceCoordinates;
        const countryCodes = (tripCountries ?? []).map(c => c.code);
        const countryNames = (tripCountries ?? []).map(c => c.name);

        // A pin-only source leaves the item with no address. Resolve one before
        // searching: it becomes the item's address *and* turns a bare pin name
        // into a query Places can actually match.
        let resolvedAddress: string | undefined;
        if (!address?.trim() && source) {
            resolvedAddress = await this.reverseGeocode(source.lat, source.lng);
        }

        const searchAddress = address?.trim() || resolvedAddress;
        const query = searchAddress ? `${name} ${searchAddress}` : name;
        console.log(`[EntityEnrichment] Enriching accommodation: "${query}"`, countryCodes.length ? `countries: [${countryCodes.join(', ')}]` : '');

        const result = await this.searchPlace(query, { countryCodes, countryNames });

        // No source coordinates to reverse-geocode: the matched place's own
        // address is the next best thing.
        if (!address?.trim() && !resolvedAddress) resolvedAddress = result?.formattedAddress;

        let lat = result?.lat ?? 0;
        let lng = result?.lng ?? 0;
        const photoRef = result?.photoRef;
        const photoUri = result?.photoUri;

        // Fallback: Places text search can miss unusual listing names (apartments,
        // long property titles). If we didn't get coordinates, geocode the address
        // directly so the item still lands on the map.
        if (lat === 0 && lng === 0 && searchAddress) {
            const geo = await this.geocodeAddress(searchAddress, { countryCodes, countryNames });
            if (geo) { lat = geo.lat; lng = geo.lng; }
        }

        // Nothing resolved at all (no place, no photo, no geocode, no address) — give up.
        if (lat === 0 && lng === 0 && !photoRef && !resolvedAddress) return { success: false };

        const hasCoords = lat !== 0 || lng !== 0;

        // Upload Google Places photo to Firebase Storage to get a permanent URL.
        // Falls back to the CDN URI if Firebase upload fails (e.g. CORS on lh3.googleusercontent.com).
        let photoUrl: string | undefined;
        if (photoRef) {
            const storagePath = `trips/${tripId}/accommodations/${accommodationId}`;
            console.log('[EntityEnrichment] Uploading accommodation photo → storagePath:', storagePath);
            photoUrl = await ImageStorageService.uploadGooglePlacesPhotoByRef(
                photoRef,
                storagePath,
            ) ?? undefined;
            console.log('[EntityEnrichment] uploadGooglePlacesPhotoByRef result:', photoUrl ? '✅ ' + photoUrl.substring(0, 80) : '❌ null');
            if (!photoUrl && photoUri) {
                console.warn('[EntityEnrichment] Firebase upload failed — using CDN URI directly for accommodation:', accommodationId);
                photoUrl = photoUri;
            }
        } else {
            console.warn('[EntityEnrichment] No photoRef — skipping image upload for accommodation:', accommodationId);
        }
        console.log('[EntityEnrichment] Final photoUrl for accommodation:', photoUrl ? photoUrl.substring(0, 80) + '...' : 'NONE');

        try {
            const accommodationRef = doc(
                db,
                `trips/${tripId}/accommodations`,
                accommodationId
            );

            const updates: Record<string, any> = {};

            if (hasCoords && !source) {
                // Update the location sub-object with real coordinates
                updates['location.lat'] = lat;
                updates['location.lng'] = lng;
                updates['location.latitude'] = lat;
                updates['location.longitude'] = lng;
            }

            // Only ever fills a blank — a user-entered address is never replaced.
            // Both spellings are written: the card reads `address`, the map and
            // the editor read `location.address`.
            if (resolvedAddress) {
                updates['address'] = resolvedAddress;
                updates['location.address'] = resolvedAddress;
            }

            if (photoUrl) {
                // Firestore: always set images array with the single enriched URL
                // (will be merged/appended if the accommodation already has images
                //  — but for auto-created items images is typically empty)
                updates['images'] = [photoUrl];
            }

            if (Object.keys(updates).length > 0) {
                console.log('[EntityEnrichment] Firestore updateDoc for accommodation', accommodationId, '→ fields:', Object.keys(updates));
                await updateDoc(accommodationRef, updates);
                console.log(
                    `[EntityEnrichment] ✅ Accommodation enriched: ${accommodationId}`,
                    { lat, lng, hasPhoto: !!photoUrl }
                );
            } else {
                console.warn('[EntityEnrichment] No updates to write for accommodation:', accommodationId, { hasCoords, hasPhoto: !!photoUrl });
            }

            return { latitude: lat, longitude: lng, photoUrl, success: hasCoords };
        } catch (err) {
            console.error('[EntityEnrichment] Failed to update accommodation:', err);
            return { success: false };
        }
    }

    // ── Activity enrichment ───────────────────────────────────────────────────

    /**
     * Enrich an activity document with geocoordinates and a highlight image.
     *
     * @param tripId          — Firestore trip document ID
     * @param activityId      — Firestore activity document ID
     * @param name            — Activity / POI name
     * @param address         — Address or location hint (optional)
     * @param tripCountries   — ISO codes + English names of the trip's countries
     *                         used to validate returned coordinates and bias the search
     * @param options.sourceCoordinates — Coordinates supplied by the source (e.g. KML
     *                         placemarks) are authoritative: the pin is never moved,
     *                         and a missing address is resolved from them.
     */
    static async enrichActivity(
        tripId: string,
        activityId: string,
        name: string,
        address?: string,
        tripCountries?: { code: string; name: string }[],
        options?: { sourceCoordinates?: { lat: number; lng: number } }
    ): Promise<EnrichmentResult> {
        const source = options?.sourceCoordinates;
        const countryCodes = (tripCountries ?? []).map(c => c.code);
        const countryNames = (tripCountries ?? []).map(c => c.name);

        // See enrichAccommodation: a map pin arrives with coordinates only, so
        // the address has to come from them.
        let resolvedAddress: string | undefined;
        if (!address?.trim() && source) {
            resolvedAddress = await this.reverseGeocode(source.lat, source.lng);
        }

        const searchAddress = address?.trim() || resolvedAddress;
        const query = searchAddress ? `${name} ${searchAddress}` : name;
        console.log(`[EntityEnrichment] Enriching activity: "${query}"`, countryCodes.length ? `countries: [${countryCodes.join(', ')}]` : '');

        const result = await this.searchPlace(query, { countryCodes, countryNames });

        if (!address?.trim() && !resolvedAddress) resolvedAddress = result?.formattedAddress;

        let lat = result?.lat ?? 0;
        let lng = result?.lng ?? 0;
        const photoRef = result?.photoRef;
        const photoUri = result?.photoUri;

        // Fallback: geocode the address directly when Places text search yields
        // no coordinates, so the activity still appears on the map.
        if (lat === 0 && lng === 0 && searchAddress) {
            const geo = await this.geocodeAddress(searchAddress, { countryCodes, countryNames });
            if (geo) { lat = geo.lat; lng = geo.lng; }
        }

        if (lat === 0 && lng === 0 && !photoRef && !resolvedAddress) return { success: false };

        const hasCoords = lat !== 0 || lng !== 0;

        // Upload Google Places photo to Firebase Storage to get a permanent URL.
        // Falls back to the CDN URI if Firebase upload fails (e.g. CORS on lh3.googleusercontent.com).
        let photoUrl: string | undefined;
        if (photoRef) {
            const storagePath = `trips/${tripId}/activities/${activityId}`;
            console.log('[EntityEnrichment] Uploading activity photo to Firebase Storage...');
            photoUrl = await ImageStorageService.uploadGooglePlacesPhotoByRef(
                photoRef,
                storagePath,
            ) ?? undefined;
            if (!photoUrl && photoUri) {
                console.warn('[EntityEnrichment] Firebase upload failed — using CDN URI directly for activity:', activityId);
                photoUrl = photoUri;
            }
        }

        try {
            const activityRef = doc(db, `trips/${tripId}/activities`, activityId);

            const updates: Record<string, any> = {};

            if (hasCoords && !source) {
                updates['location.lat'] = lat;
                updates['location.lng'] = lng;
                updates['location.latitude'] = lat;
                updates['location.longitude'] = lng;
            }

            // Only ever fills a blank — a user-entered address is never replaced.
            if (resolvedAddress) {
                updates['location.address'] = resolvedAddress;
            }

            if (photoUrl) {
                updates['highlightImages'] = [photoUrl];
            }

            if (Object.keys(updates).length > 0) {
                await updateDoc(activityRef, updates);
                console.log(
                    `[EntityEnrichment] ✅ Activity enriched: ${activityId}`,
                    { lat, lng, hasPhoto: !!photoUrl }
                );
            }

            return { latitude: lat, longitude: lng, photoUrl, success: hasCoords };
        } catch (err) {
            console.error('[EntityEnrichment] Failed to update activity:', err);
            return { success: false };
        }
    }
}

export default EntityEnrichmentService;
